import React, { useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { _U } from "../../../modules/utils";
import "./LiveCaptions.css";
import {
  clearLiveCaptionOverlay,
  createLiveCaptionSession,
  resolveLiveCaptionInputUrl,
  startLiveCaptionSession,
  stopLiveCaptionSession,
} from "../../../modules/captions/liveCaptionApi";

const CAPTION_RUNTIME_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  FAIL_CLOSED: "fail_closed",
};

// B2 (G23/G27): Live transcript 운영 상수 — windowed 렌더링으로 대량 구간 부하 방지
const TRANSCRIPT_WINDOW_SIZE = 16;
const TRANSCRIPT_MAX_SEGMENTS = 400; // bounded — 장시간 라이브에서 무제한 적재 방지
const CONFIDENCE_HIGH = 0.85;
const CONFIDENCE_LOW = 0.7;

const getConfidenceClass = (confidence) => {
  if (!Number.isFinite(confidence)) return null;
  if (confidence >= CONFIDENCE_HIGH) return "conf_high";
  if (confidence >= CONFIDENCE_LOW) return "conf_mid";
  return "conf_low";
};

const formatTranscriptMs = (ms) => {
  if (!Number.isFinite(ms)) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const createCaptionContextState = (payload = {}) => ({
  contextResolveStatus: payload.contextResolveStatus || "pending",
  contextResolveReason: payload.contextResolveReason || null,
  blockReason: payload.blockReason || null,
  dictionaryApplied: payload.dictionaryApplied === true,
});

const getMedicalDictionaryStatusLabel = (contextState = {}) => {
  if (contextState.contextResolveStatus === "applied" && contextState.dictionaryApplied) {
    return "의료용어 사전 적용";
  }
  if (contextState.contextResolveStatus === "degraded") {
    return "의료용어 사전 미적용";
  }
  if (contextState.contextResolveStatus === "fail_closed") {
    return "의료용어 사전 오류 · Hub 송출 차단";
  }
  return "의료용어 사전 대기";
};

const buildCaptionTranscriptionRequestScope = ({
  siteIdx,
  eventIdx,
  sessionIdx,
} = {}) => ({
  siteIdx,
  eventIdx,
  sessionIdx,
  sessionId: sessionIdx ? String(sessionIdx) : undefined,
});

const featureItems = [
  {
    key: "captions",
    title: "실시간 자막",
    description: "한국어 음성을 실시간 한국어 자막으로 표시",
    status: "선택됨",
    secondaryStatus: "청중 송출 준비",
    enabled: true,
  },
  {
    key: "translate",
    title: "실시간 번역",
    description: "한국어 음성을 외국어 자막으로 변환해 표시",
    status: "준비중",
    enabled: false,
  },
  {
    key: "interpretation",
    title: "실시간 통역",
    description: "음성을 통역 오디오 또는 통역 채널로 제공",
    status: "준비중",
    enabled: false,
  },
];

const CaptionStatusBadge = ({ children, muted = false }) => (
  <div className="on_off_exporsure">
    <strong className={cx({ off: muted })}>{children}</strong>
  </div>
);

export const LiveCaptionsFeatureList = ({
  selectedFeature = "captions",
  onSelectFeature,
  eventIdx,
  sessionIdx,
  siteIdx,
}) => {
  const featureListCaptureRef = useRef({ active: false });
  const featureListCaptionSessionRef = useRef(null);
  const [featureEnabledMap, setFeatureEnabledMap] = useState({
    captions: false,
    translate: false,
    interpretation: false,
  });
  const [featureStatusMap, setFeatureStatusMap] = useState({
    captions: "꺼짐",
  });

  useEffect(() => {
    return () => {
      featureListCaptureRef.current.active = false;
      clearLiveCaptionOverlay();
    };
  }, []);

  const stopFeatureListCaptions = async () => {
    featureListCaptureRef.current.active = false;
    clearLiveCaptionOverlay();
    const captionSession = featureListCaptionSessionRef.current;
    featureListCaptionSessionRef.current = null;
    if (captionSession?.id) {
      try {
        await stopLiveCaptionSession({
          captionSessionId: captionSession.id,
          ...buildCaptionTranscriptionRequestScope({ siteIdx, eventIdx, sessionIdx }),
        });
      } catch (_) {}
    }
    setFeatureStatusMap((current) => ({
      ...current,
      captions: "꺼짐",
    }));
  };

  const startFeatureListCaptions = async () => {
    if (featureListCaptureRef.current.active) {
      return;
    }

    featureListCaptureRef.current.active = true;
    setFeatureStatusMap((current) => ({
      ...current,
      captions: "자막 준비 중",
    }));

    try {
      const scope = buildCaptionTranscriptionRequestScope({ siteIdx, eventIdx, sessionIdx });
      const inputUrl = resolveLiveCaptionInputUrl();
      const captionSession = await createLiveCaptionSession({
        ...scope,
        mode: "auto",
        inputUrl,
      });
      featureListCaptionSessionRef.current = captionSession;
      const started = await startLiveCaptionSession({
        ...scope,
        captionSessionId: captionSession.id,
        inputUrl,
      });
      featureListCaptionSessionRef.current = started?.session || captionSession;

      setFeatureStatusMap((current) => ({
        ...current,
        captions: "연속 생성 중",
      }));
    } catch (error) {
      featureListCaptureRef.current.active = false;
      featureListCaptionSessionRef.current = null;
      setFeatureEnabledMap((current) => ({
        ...current,
        captions: false,
      }));
      setFeatureStatusMap((current) => ({
        ...current,
        captions: "fail-closed",
      }));
    }
  };

  const handleFeatureToggle = (event, item) => {
    event.stopPropagation();
    if (!item.enabled) {
      return;
    }
    const nextEnabled = !featureEnabledMap[item.key];
    setFeatureEnabledMap((current) => ({
      ...current,
      [item.key]: nextEnabled,
    }));

    if (item.key === "captions") {
      if (nextEnabled) {
        startFeatureListCaptions();
      } else {
        stopFeatureListCaptions();
      }
    }
  };

  return (
    <>
      <div className="top_title">
        <span>실시간 기능 · 하나의 기능만 선택 가능</span>
      </div>
      <ul className="control_content_list live_captions_content_list">
        {featureItems.map((item) => {
          const selected = item.key === selectedFeature;
          const enabled = !!featureEnabledMap[item.key] && item.enabled;

          return (
            <li key={item.key} className={cx({ selected })}>
              <a onClick={(event) => item.enabled && onSelectFeature && onSelectFeature(event, item)}>
                <span>{item.title}</span>
              </a>
              <p className="live_captions_item_description">{item.description}</p>
              <div className="control_content_list_tool">
                <CaptionStatusBadge muted={!enabled}>
                  {enabled ? featureStatusMap[item.key] || item.status : item.enabled ? "꺼짐" : item.status}
                </CaptionStatusBadge>
                {item.secondaryStatus && selected && enabled ? (
                  <CaptionStatusBadge>{item.secondaryStatus}</CaptionStatusBadge>
                ) : null}
                <label className="switch">
                  <input
                    checked={enabled}
                    data-live-captions-feature-list-toggle
                    onChange={(event) => handleFeatureToggle(event, item)}
                    type="checkbox"
                  />
                  <span />
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

const runtimeFrames = [
  {
    action: "caption:partial",
    label: "partial",
    text: "운영자 자막 스트림에만 표시됩니다.",
  },
  {
    action: "caption:final",
    label: "final",
    text: "final만 Hub 청중에게 송출됩니다.",
  },
];

export const LiveCaptionsDetail = ({ eventIdx, sessionIdx, siteIdx }) => {
  const continuousCaptureRef = useRef({ active: false });
  const detailCaptionSessionRef = useRef(null);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [captionMode, setCaptionMode] = useState("auto");
  const [runtimeStatus, setRuntimeStatus] = useState("idle");
  const [operatorStreamVisible, setOperatorStreamVisible] = useState(true);
  const [runtimeStream, setRuntimeStream] = useState(runtimeFrames);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isContinuousRunning, setIsContinuousRunning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [contextState, setContextState] = useState(createCaptionContextState());
  const contextStateRef = useRef(createCaptionContextState());
  // B2: Live transcript (G23) + mode=off 카운트다운 (G26) + 단축키 (G29)
  const searchInputRef = useRef(null);
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [livePartial, setLivePartial] = useState(null);
  const [transcriptWindowStart, setTranscriptWindowStart] = useState(0);
  const [lowConfidenceOnly, setLowConfidenceOnly] = useState(false);
  const [speakerFilter, setSpeakerFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [modeOffCountdown, setModeOffCountdown] = useState(null);

  // 상태값(wire)은 fail_closed로 통일(API/Hub/connectionResolver 계약) — "fail-closed"는 UI 라벨로만 사용
  const isFailClosed = runtimeStatus === CAPTION_RUNTIME_STATUS.FAIL_CLOSED;
  const isRunning = featureEnabled && runtimeStatus === "running";
  const medicalDictionaryStatusLabel = getMedicalDictionaryStatusLabel(contextState);

  useEffect(() => {
    return () => {
      continuousCaptureRef.current.active = false;
      clearLiveCaptionOverlay();
    };
  }, []);

  const appendRuntimeFrame = (frame) => {
    setRuntimeStream((prevFrames) => [frame, ...prevFrames].slice(0, 5));
  };

  const applyCaptionContextState = (context = {}) => {
    const nextContextState = createCaptionContextState(context);
    contextStateRef.current = nextContextState;
    setContextState(nextContextState);
    return nextContextState;
  };

  const publishRuntimeState = ({
    context = contextState,
  } = {}) => {
    applyCaptionContextState(context);
    return true;
  };

  // G23: WS caption 프레임을 transcript에 적재 — Socket.js의 SOCKET-RECEIVED envelope 구독
  // (ControlPanel.shared.js onReceivedMessage와 동일 파싱 패턴)
  useEffect(() => {
    const appendFinalSegment = (payload = {}, corrected = false) => {
      if (!payload.text) return;
      setLiveTranscript((prevSegments) => {
        const segmentId = String(payload.segmentId || payload.captionSegmentId || Date.now());
        const nextSegment = {
          id: segmentId,
          text: String(payload.text),
          confidence: Number.isFinite(payload.confidence) ? payload.confidence : null,
          speaker: payload.speaker || payload.speakerLabel || null,
          startMs: Number.isFinite(payload.startMs) ? payload.startMs : null,
          corrected,
        };
        const existingIdx = prevSegments.findIndex((segment) => segment.id === segmentId);
        if (existingIdx >= 0) {
          const next = prevSegments.slice();
          next[existingIdx] = { ...next[existingIdx], ...nextSegment, corrected: true };
          return next;
        }
        return [nextSegment, ...prevSegments].slice(0, TRANSCRIPT_MAX_SEGMENTS);
      });
    };

    const handleTranscriptFrame = (frame = {}) => {
      const payload = frame.data || {};
      if (frame.action === "caption:partial") {
        setLivePartial(
          payload.text ? { text: String(payload.text), confidence: payload.confidence } : null
        );
        return;
      }
      if (frame.action === "caption:final") {
        setLivePartial(null);
        appendFinalSegment(payload);
        return;
      }
      if (frame.action === "caption:correction") {
        appendFinalSegment(payload, true);
        return;
      }
      if (frame.action === "caption:state" && payload.status === "fail_closed") {
        setRuntimeStatus(CAPTION_RUNTIME_STATUS.FAIL_CLOSED);
        setLivePartial(null);
      }
    };

    const onReceivedMessage = (e) => {
      _U.parseMessage(e)
        .then((data) => {
          switch (data.code) {
            case "SOCKET-RECEIVED":
              return data.data ? JSON.parse(data.data) : null;
            default:
              return null;
          }
        })
        .then((frame) => {
          if (frame && String(frame.action || "").startsWith("caption:")) {
            handleTranscriptFrame(frame);
          }
        })
        .catch(() => {});
    };

    window.addEventListener("message", onReceivedMessage);
    return () => window.removeEventListener("message", onReceivedMessage);
  }, []);

  const runCurrentMediaTranscription = async () => {
    if (!featureEnabled || captionMode === "off") {
      setRuntimeStatus("idle");
      clearLiveCaptionOverlay();
      publishRuntimeState({
        mode: "off",
        status: CAPTION_RUNTIME_STATUS.IDLE,
      });
      return null;
    }

    setIsCapturing(true);
    setErrorMessage("");

    try {
      const inputUrl = resolveLiveCaptionInputUrl();
      appendRuntimeFrame({
        action: "caption:state",
        label: "state",
        text: `입력 소스 확인됨 · ${inputUrl}`,
      });
      return { inputUrl };
    } catch (error) {
      const message = error?.message || "현재 영상 자막 입력 소스 확인에 실패했습니다.";
      const failClosedContext = createCaptionContextState({
        contextResolveStatus: "fail_closed",
        blockReason: "input_url_unavailable",
        dictionaryApplied: false,
      });
      setRuntimeStatus(CAPTION_RUNTIME_STATUS.FAIL_CLOSED);
      applyCaptionContextState(failClosedContext);
      setErrorMessage(message);
      appendRuntimeFrame({
        action: "caption:state",
        label: "state",
        text: `fail-closed · ${message}`,
      });
      publishRuntimeState({
        status: CAPTION_RUNTIME_STATUS.FAIL_CLOSED,
        context: failClosedContext,
      });
      return null;
    } finally {
      setIsCapturing(false);
    }
  };

  const stopActiveCaptionSession = async () => {
    const captionSession = detailCaptionSessionRef.current;
    detailCaptionSessionRef.current = null;

    if (captionSession?.id) {
      await stopLiveCaptionSession({
        captionSessionId: captionSession.id,
        ...buildCaptionTranscriptionRequestScope({ siteIdx, eventIdx, sessionIdx }),
      });
    }
  };

  const stopLiveCaptions = async () => {
    continuousCaptureRef.current.active = false;
    setIsContinuousRunning(false);
    clearLiveCaptionOverlay();

    try {
      await stopActiveCaptionSession();
      setRuntimeStatus("idle");
      appendRuntimeFrame({
        action: "caption:state",
        label: "state",
        text: "실시간 자막 세션이 중지되었습니다.",
      });
      publishRuntimeState({
        status: CAPTION_RUNTIME_STATUS.IDLE,
        context: contextStateRef.current,
      });
    } catch (error) {
      setErrorMessage(error?.message || "실시간 자막 중지에 실패했습니다.");
    }
  };

  const startLiveCaptions = async () => {
    if (isContinuousRunning) {
      return;
    }

    continuousCaptureRef.current.active = true;
    setIsContinuousRunning(true);
    setIsCapturing(true);
    setRuntimeStatus("running");
    setErrorMessage("");
    appendRuntimeFrame({
      action: "caption:partial",
      label: "partial",
      text: "운영용 실시간 자막 세션을 시작합니다.",
    });

    try {
      const scope = buildCaptionTranscriptionRequestScope({ siteIdx, eventIdx, sessionIdx });
      const inputUrl = resolveLiveCaptionInputUrl();
      const captionSession = await createLiveCaptionSession({
        ...scope,
        mode: captionMode,
        inputUrl,
      });
      detailCaptionSessionRef.current = captionSession;
      const started = await startLiveCaptionSession({
        ...scope,
        captionSessionId: captionSession.id,
        inputUrl,
      });
      const startedSession = started?.session || captionSession;
      detailCaptionSessionRef.current = startedSession;
      applyCaptionContextState(startedSession.contextMetadata || startedSession);
      appendRuntimeFrame({
        action: "caption:state",
        label: "state",
        text: "Live Caption Worker 시작 요청이 전송되었습니다.",
      });
      return started;
    } catch (error) {
      const message = error?.message || "실시간 자막 시작에 실패했습니다.";
      const failClosedContext = createCaptionContextState({
        contextResolveStatus: "fail_closed",
        blockReason: "caption_start_failed",
        dictionaryApplied: false,
      });
      continuousCaptureRef.current.active = false;
      setIsContinuousRunning(false);
      setRuntimeStatus(CAPTION_RUNTIME_STATUS.FAIL_CLOSED);
      applyCaptionContextState(failClosedContext);
      setErrorMessage(message);
      appendRuntimeFrame({
        action: "caption:state",
        label: "state",
        text: `fail-closed · ${message}`,
      });
      return null;
    } finally {
      setIsCapturing(false);
    }
  };

  const toggleFeatureEnabled = () => {
    const nextEnabled = !featureEnabled;

    setFeatureEnabled(nextEnabled);
    if (!nextEnabled) {
      stopLiveCaptions();
    } else {
      setRuntimeStatus("idle");
      publishRuntimeState({
        status: CAPTION_RUNTIME_STATUS.IDLE,
        context: createCaptionContextState(),
      });
    }
  };

  const applyModeOff = () => {
    setModeOffCountdown(null);
    setCaptionMode("off");
    continuousCaptureRef.current.active = false;
    setIsContinuousRunning(false);
    setRuntimeStatus("idle");
    clearLiveCaptionOverlay();
    stopLiveCaptions();
    publishRuntimeState({
      mode: "off",
      status: CAPTION_RUNTIME_STATUS.IDLE,
    });
  };

  const selectCaptionMode = (mode) => {
    if (mode === "off") {
      if (captionMode === "off") {
        return;
      }
      // G26: 즉시 차단하지 않고 5초 카운트다운 confirmation을 거친다.
      // fail-closed/quota 자동 차단(triggerFailClosed)은 모달 없이 즉시 동작한다.
      setModeOffCountdown(5);
      return;
    }
    setModeOffCountdown(null);
    setCaptionMode(mode);
    publishRuntimeState({
      mode,
      status: runtimeStatus,
    });
  };

  // G26: 카운트다운 진행 — 0 도달 시 적용, 취소 가능
  useEffect(() => {
    if (modeOffCountdown === null) {
      return undefined;
    }
    if (modeOffCountdown <= 0) {
      applyModeOff();
      return undefined;
    }
    const timer = setTimeout(() => {
      setModeOffCountdown((current) => (current === null ? null : current - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [modeOffCountdown]);

  // G29: 키보드 단축키 — Space(시작/중지) J/K(window 이동) Cmd+E(검색 포커스) Esc(카운트다운 취소) Cmd+M(mode 전환)
  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target || {};
      const tagName = String(target.tagName || "").toLowerCase();
      const isTyping = tagName === "input" || tagName === "textarea" || target.isContentEditable === true;
      if (event.key === "Escape") {
        setModeOffCountdown(null);
        return;
      }
      if (isTyping) {
        return;
      }
      const withCmd = event.metaKey || event.ctrlKey;
      if (withCmd && (event.key === "e" || event.key === "E")) {
        event.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        return;
      }
      if (withCmd && (event.key === "m" || event.key === "M")) {
        event.preventDefault();
        selectCaptionMode(captionMode === "auto" ? "off" : "auto");
        return;
      }
      if (event.code === "Space") {
        event.preventDefault();
        if (isContinuousRunning) {
          stopLiveCaptions();
        } else {
          startLiveCaptions();
        }
        return;
      }
      if (event.key === "j" || event.key === "J") {
        setTranscriptWindowStart((start) => start + TRANSCRIPT_WINDOW_SIZE);
        return;
      }
      if (event.key === "k" || event.key === "K") {
        setTranscriptWindowStart((start) => Math.max(0, start - TRANSCRIPT_WINDOW_SIZE));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // G23: 필터/검색 + windowed 렌더링 파생값
  const transcriptSpeakers = useMemo(() => {
    const counts = new Map();
    liveTranscript.forEach((segment) => {
      if (!segment.speaker) return;
      counts.set(segment.speaker, (counts.get(segment.speaker) || 0) + 1);
    });
    return Array.from(counts.entries()).slice(0, 4);
  }, [liveTranscript]);

  const lowConfidenceCount = useMemo(
    () =>
      liveTranscript.filter(
        (segment) => Number.isFinite(segment.confidence) && segment.confidence < CONFIDENCE_LOW
      ).length,
    [liveTranscript]
  );

  const filteredTranscript = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return liveTranscript.filter((segment) => {
      if (
        lowConfidenceOnly &&
        !(Number.isFinite(segment.confidence) && segment.confidence < CONFIDENCE_LOW)
      ) {
        return false;
      }
      if (speakerFilter && segment.speaker !== speakerFilter) {
        return false;
      }
      if (query) {
        const timeText = formatTranscriptMs(segment.startMs);
        if (!segment.text.toLowerCase().includes(query) && !timeText.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [liveTranscript, lowConfidenceOnly, speakerFilter, searchQuery]);

  const maxWindowStart = Math.max(0, filteredTranscript.length - TRANSCRIPT_WINDOW_SIZE);
  const effectiveWindowStart = Math.min(transcriptWindowStart, maxWindowStart);
  const visibleTranscript = filteredTranscript.slice(
    effectiveWindowStart,
    effectiveWindowStart + TRANSCRIPT_WINDOW_SIZE
  );
  const hasPrevWindow = effectiveWindowStart > 0;
  const hasNextWindow = effectiveWindowStart + TRANSCRIPT_WINDOW_SIZE < filteredTranscript.length;

  const triggerFailClosed = () => {
    continuousCaptureRef.current.active = false;
    setIsContinuousRunning(false);
    setRuntimeStatus(CAPTION_RUNTIME_STATUS.FAIL_CLOSED);
    setErrorMessage("운영자가 긴급 차단했습니다.");
    clearLiveCaptionOverlay();
    stopActiveCaptionSession().catch((error) => {
      setErrorMessage(error?.message || "실시간 자막 중지에 실패했습니다.");
    });
    publishRuntimeState({
      status: CAPTION_RUNTIME_STATUS.FAIL_CLOSED,
      context: {
        contextResolveStatus: "fail_closed",
        blockReason: "operator_block",
        dictionaryApplied: false,
      },
    });
  };

  return (
    <div className="live_captions_detail">
      <div>
        <h4>실시간 자막</h4>
      </div>
      {/* G22: 상단 풀폭 송출 상태 bar — auto(녹색) / off(빨강) / fail-closed(빨강 펄스) */}
      <div
        className={cx(
          "live_captions_status_bar",
          isFailClosed ? "fail_closed" : captionMode === "off" ? "off" : "auto"
        )}
        data-live-captions-status-bar
        role="status"
      >
        <span className="live_captions_status_bar_dot" />
        <strong>
          {isFailClosed
            ? "자막 일시 중지 · fail-closed"
            : captionMode === "off"
            ? "CC 차단 · off"
            : "CC 송출 중 · auto"}
        </strong>
        <span>
          {isFailClosed
            ? "청중 송출 자동 차단 · 운영자 확인 필요"
            : captionMode === "off"
            ? "청중 자막 없음 · Console/DB/audit만 유지"
            : "final만 청중 송출 · partial은 운영자 전용"}
        </span>
      </div>
      <div className="live_captions_feature_toggle">
        <ul>
          <li>
            <h5>기능 사용</h5>
            <div className="connect_way_div_inner">
              <label className="switch">
                <input
                  checked={featureEnabled}
                  data-live-captions-feature-toggle
                  onChange={toggleFeatureEnabled}
                  type="checkbox"
                />
                <span />
              </label>
              <span>{featureEnabled ? "사용중" : "꺼짐"}</span>
            </div>
          </li>
        </ul>
      </div>
      <div className="live_captions_runtime_controls">
        <a
          data-live-captions-start
          role="button"
          onClick={startLiveCaptions}
        >
          {isContinuousRunning || isCapturing ? "연속 생성 중" : "연속 시작"}
        </a>
        <a
          data-live-captions-stop
          role="button"
          onClick={stopLiveCaptions}
        >
          중지
        </a>
        <a
          data-live-captions-fail-closed
          role="button"
          onClick={triggerFailClosed}
        >
          긴급 차단
        </a>
        <a
          data-live-captions-current-media
          role="button"
          onClick={runCurrentMediaTranscription}
        >
          1회 테스트
        </a>
      </div>
      <div className="live_captions_mode_controls">
        {["auto", "off"].map((mode) => (
          <a
            key={mode}
            role="button"
            className={cx({ selected: captionMode === mode })}
            onClick={() => selectCaptionMode(mode)}
          >
            {mode}
          </a>
        ))}
      </div>
      {/* G26: mode=off 5초 카운트다운 confirmation — 취소 가능, fail-closed 자동 차단은 모달 없음 */}
      {modeOffCountdown !== null ? (
        <div
          className="live_captions_mode_off_confirm"
          data-live-captions-mode-off-countdown
          role="alertdialog"
          aria-live="assertive"
        >
          <p>
            <strong>{modeOffCountdown}초</strong> 후 청중 자막 송출이 차단됩니다. Esc 또는 취소로 중단할 수
            있습니다.
          </p>
          <div className="live_captions_mode_off_confirm_actions">
            <a
              data-live-captions-mode-off-cancel
              role="button"
              onClick={() => setModeOffCountdown(null)}
            >
              취소
            </a>
            <a data-live-captions-mode-off-apply role="button" onClick={applyModeOff}>
              즉시 차단
            </a>
          </div>
        </div>
      ) : null}
      <div>
        <ul className="live_captions_status_list">
          <li>
            <h5>선택 기능</h5>
            <div><span>실시간 자막</span></div>
          </li>
          <li>
            <h5>상태</h5>
            <div><span>{isRunning ? "RUNNING" : isFailClosed ? "FAIL-CLOSED" : runtimeStatus.toUpperCase()}</span></div>
          </li>
          <li>
            <h5>입력 소스</h5>
            <div><span>Live audio · JW/FMS audio tap</span></div>
          </li>
          <li>
            <h5>출력 방식</h5>
            <div><span>한국어 자막 · final만 Hub 청중에게 송출</span></div>
          </li>
          <li>
            <h5>운영자 자막 스트림</h5>
            <div className="connect_way_div_inner">
              <label className="switch">
                <input
                  checked={operatorStreamVisible}
                  onChange={() => setOperatorStreamVisible(!operatorStreamVisible)}
                  type="checkbox"
                />
                <span />
              </label>
              <span>{operatorStreamVisible ? "표시중" : "숨김"}</span>
            </div>
          </li>
          <li>
            <h5>선택 제한</h5>
            <div><span>실시간 자막 / 번역 / 통역 중 1개만 활성화</span></div>
          </li>
          <li>
            <h5>운영 상태</h5>
            <div>
              <span>
                {isFailClosed
                  ? "fail-closed · Hub 송출 차단"
                  : `OpenAI 정상 · ${medicalDictionaryStatusLabel} · latency p95 대기`}
              </span>
            </div>
          </li>
          <li>
            <h5>의료용어 사전</h5>
            <div>
              <span>
                {medicalDictionaryStatusLabel}
                {contextState.contextResolveReason ? ` · ${contextState.contextResolveReason}` : ""}
              </span>
            </div>
          </li>
          <li>
            <h5>송출 mode</h5>
            <div><span>{captionMode} · {isFailClosed ? "fail-closed" : "fail-closed 아님"}</span></div>
          </li>
          <li>
            <h5>보정</h5>
            <div><span>caption:correction · 이전 final segment 대체</span></div>
          </li>
        </ul>
      </div>
      <div className="live_captions_runtime_stream">
        <h5>운영자 자막 스트림</h5>
        {errorMessage ? <p>{errorMessage}</p> : null}
        <ul aria-live="polite">
          {operatorStreamVisible && runtimeStream.map((frame, index) => (
            <li key={`${frame.action}-${index}`}>
              <strong>{frame.label}</strong>
              <span>{frame.action}</span>
              <p>{frame.text}</p>
            </li>
          ))}
        </ul>
      </div>
      {/* G23: Live transcript — 필터(신뢰도/화자) + 검색 + windowed 렌더링, G27 confidence 색상 */}
      {operatorStreamVisible ? (
        <div className="live_captions_transcript" data-live-captions-transcript>
          <h5>Live transcript</h5>
          <div className="live_captions_transcript_toolbar">
            <a
              className={cx("live_captions_filter_chip", "low_confidence", {
                selected: lowConfidenceOnly,
              })}
              data-live-captions-filter-low-confidence
              role="button"
              onClick={() => {
                setLowConfidenceOnly(!lowConfidenceOnly);
                setTranscriptWindowStart(0);
              }}
            >
              신뢰도 &lt; 0.7 · {lowConfidenceCount}
            </a>
            {transcriptSpeakers.map(([speaker, count]) => (
              <a
                key={speaker}
                className={cx("live_captions_filter_chip", { selected: speakerFilter === speaker })}
                data-live-captions-filter-speaker
                role="button"
                onClick={() => {
                  setSpeakerFilter(speakerFilter === speaker ? null : speaker);
                  setTranscriptWindowStart(0);
                }}
              >
                {speaker} · {count}
              </a>
            ))}
            <input
              ref={searchInputRef}
              aria-label="자막 본문 또는 시간 검색"
              data-live-captions-transcript-search
              placeholder="자막 본문 또는 시간 검색 (예: 메로페넘 / 00:42)"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setTranscriptWindowStart(0);
              }}
            />
          </div>
          <ul
            aria-live="polite"
            className="live_captions_transcript_list"
            data-render-strategy="virtualized"
            data-live-captions-transcript-list
            data-visible-window-start={effectiveWindowStart}
            data-total-segment-count={filteredTranscript.length}
          >
            {livePartial ? (
              <li className="partial" data-live-captions-transcript-partial>
                <div className="live_captions_transcript_meta">
                  <span>partial · 운영자 전용</span>
                  {getConfidenceClass(livePartial.confidence) ? (
                    <span
                      className={cx(
                        "live_captions_conf_chip",
                        getConfidenceClass(livePartial.confidence)
                      )}
                    >
                      {Number(livePartial.confidence).toFixed(2)}
                    </span>
                  ) : null}
                </div>
                <p>{livePartial.text}</p>
              </li>
            ) : null}
            {visibleTranscript.map((segment) => (
              <li key={segment.id}>
                <div className="live_captions_transcript_meta">
                  <span>{formatTranscriptMs(segment.startMs)}</span>
                  <span>{segment.corrected ? "correction" : "final"}</span>
                  {getConfidenceClass(segment.confidence) ? (
                    <span
                      className={cx(
                        "live_captions_conf_chip",
                        getConfidenceClass(segment.confidence)
                      )}
                    >
                      {Number(segment.confidence).toFixed(2)}
                    </span>
                  ) : null}
                  {segment.speaker ? (
                    <span className="live_captions_speaker_chip">{segment.speaker}</span>
                  ) : null}
                </div>
                <p>{segment.text}</p>
              </li>
            ))}
            {!livePartial && visibleTranscript.length === 0 ? (
              <li data-live-captions-transcript-empty>
                <p>자막 대기 중 · 수신된 final 자막이 여기에 쌓입니다.</p>
              </li>
            ) : null}
          </ul>
          <div className="live_captions_transcript_window_nav">
            <a
              aria-disabled={!hasPrevWindow}
              className={cx({ "is-disabled": !hasPrevWindow })}
              data-live-captions-window-prev
              role="button"
              onClick={() =>
                setTranscriptWindowStart(Math.max(0, effectiveWindowStart - TRANSCRIPT_WINDOW_SIZE))
              }
            >
              이전 window (K)
            </a>
            <a
              aria-disabled={!hasNextWindow}
              className={cx({ "is-disabled": !hasNextWindow })}
              data-live-captions-window-next
              role="button"
              onClick={() => setTranscriptWindowStart(effectiveWindowStart + TRANSCRIPT_WINDOW_SIZE)}
            >
              다음 window (J)
            </a>
            <span>
              {filteredTranscript.length
                ? `${effectiveWindowStart + 1}–${Math.min(
                    effectiveWindowStart + TRANSCRIPT_WINDOW_SIZE,
                    filteredTranscript.length
                  )} / ${filteredTranscript.length}`
                : "0 / 0"}
            </span>
          </div>
          <p className="live_captions_shortcut_hint" data-live-captions-shortcut-hint>
            단축키: Space 시작/중지 · J/K window 이동 · Cmd+E 검색 · Esc 취소 · Cmd+M mode 전환
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default function LiveCaptionsPanel(props) {
  return <LiveCaptionsFeatureList {...props} />;
}
